// Client-side avatar compression pipeline.
//
// Takes a user-picked File (from an <input type="file">), draws it to a
// canvas at 256×256 (center-cropped so portraits and landscapes both
// end up as clean squares), and re-encodes as WebP at q60. Typical
// output: 5–15KB per avatar.
//
// Returns a data URL string ready for `PUT /members/:id/avatar` — the
// server route parses it, strips the base64 payload, and stores the
// bytes in core.UserAvatars.
//
// WebP support: all modern browsers (Chrome 32+, Firefox 65+, Safari
// 14+, Edge 79+). Older Safari (< 14) falls back to JPEG q75 which is
// larger but still fine for club-sized member counts.

const OUTPUT_SIZE = 256;   // final square edge in px
const WEBP_QUALITY = 0.60;
const JPEG_QUALITY = 0.75;

async function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image.")); };
    img.src = url;
  });
}

function centerCropToSquare(ctx, img, size) {
  const src = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth  - src) / 2;
  const sy = (img.naturalHeight - src) / 2;
  ctx.drawImage(img, sx, sy, src, src, 0, 0, size, size);
}

function canvasToDataUrl(canvas) {
  // Prefer WebP for the smallest bytes. If the browser refuses the
  // format (returns "data:," for unsupported), fall back to JPEG.
  const webp = canvas.toDataURL("image/webp", WEBP_QUALITY);
  if (webp && webp.startsWith("data:image/webp")) return webp;
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

export async function compressAvatar(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) {
    throw new Error("Please pick an image file.");
  }
  // Guardrail — refuse anything bigger than 20MB before we even try to
  // decode it, so a 100MB DSLR upload doesn't OOM the browser.
  if (file.size > 20 * 1024 * 1024) {
    throw new Error("Image is too large (max 20MB before compression).");
  }
  const img = await fileToImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  // High-quality downscale
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  centerCropToSquare(ctx, img, OUTPUT_SIZE);
  return canvasToDataUrl(canvas);
}

// Receipt-image compression pipeline (aspect-preserving).
//
// Receipts are portrait / tall; the avatar's center-crop-to-square
// treatment would obliterate half the line items. This variant keeps
// the natural aspect ratio, downscales so the LONG edge is at most
// RECEIPT_MAX_EDGE px, and encodes as JPEG q0.85 for reliable OCR
// input at a manageable payload size (~150–400KB for a 4032×3024
// phone photo).
const RECEIPT_MAX_EDGE = 1600;
const RECEIPT_JPEG_QUALITY = 0.85;

export async function compressReceipt(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) {
    throw new Error("Please pick an image of the receipt.");
  }
  if (file.size > 20 * 1024 * 1024) {
    throw new Error("Image is too large (max 20MB).");
  }
  const img = await fileToImage(file);
  const w = img.naturalWidth  || img.width;
  const h = img.naturalHeight || img.height;
  const scale = Math.min(1, RECEIPT_MAX_EDGE / Math.max(w, h));
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, outW, outH);
  // Azure Document Intelligence handles JPEG best; skip WebP here.
  return canvas.toDataURL("image/jpeg", RECEIPT_JPEG_QUALITY);
}

// Build the <img src> URL for a member. Uses `avatarUpdatedAt` from the
// listMembers response as a cache-buster query param so a fresh upload
// invalidates any browser cache instantly.
export function avatarSrc(member, apiBase) {
  if (!member?.avatarUpdatedAt) return null;
  const base = apiBase || (import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api/v1");
  const ts = new Date(member.avatarUpdatedAt).getTime();
  return `${base}/members/${member.id}/avatar?v=${ts}`;
}
